// ImageLightbox.tsx — a full-screen zoomable viewer for lesson images.
//
// Several lessons carry infographics with text baked into the image. At the
// inline size they're unreadable, and the app's viewport disables native pinch
// (maximum-scale=1.0), so we provide our own zoom: tap the image to step
// through fit → 2× → 3×, panning the overflow to read every corner. Tap the
// dark surround or the close button to dismiss.
import React from 'react';
import { Pressable } from './ui';

const SCALES = [1, 2, 3] as const;

export function ImageLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  const [scale, setScale] = React.useState<number>(1);
  const cycleZoom = () => setScale((s) => SCALES[(SCALES.indexOf(s as 1) + 1) % SCALES.length]);

  // Close on Escape (keyboard / external display) without stealing app focus.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(20,14,8,0.94)',
        animation: 'rise .24s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      {/* scroll/pan surface — the image grows past the viewport when zoomed */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          alignItems: scale === 1 ? 'center' : 'flex-start',
          justifyContent: 'center',
          padding: 'calc(var(--safe-top) + 8px) 8px calc(var(--safe-bottom) + 8px)',
        }}
      >
        <img
          src={src}
          alt=""
          onClick={(e) => {
            e.stopPropagation(); // tapping the image zooms; only the surround closes
            cycleZoom();
          }}
          style={{
            display: 'block',
            // Fit view (scale 1): bound by BOTH width and height so the whole image
            // fits the viewport — a tall/portrait image is limited by height (was
            // width:100% only, which made tall images overflow hugely on wide/web
            // viewports). Zoom (2x/3x) keeps width:N00% and overflows for panning.
            width: scale === 1 ? 'auto' : `${scale * 100}%`,
            height: 'auto',
            maxWidth: scale === 1 ? '100%' : 'none',
            maxHeight: scale === 1 ? '100%' : 'none',
            margin: scale === 1 ? 'auto' : '0 auto',
            objectFit: 'contain',
            borderRadius: scale === 1 ? 12 : 0,
            background: '#fff',
            cursor: 'zoom-in',
          }}
        />
      </div>

      {/* close */}
      <Pressable
        as="button"
        onClick={onClose}
        ariaLabel="Close image"
        style={{
          position: 'absolute',
          top: 'calc(var(--safe-top) + 10px)',
          right: 12,
          width: 44,
          height: 44,
          borderRadius: 999,
          border: '1px solid rgba(255,248,236,0.28)',
          background: 'rgba(20,14,8,0.5)',
          color: '#FBF1DE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </Pressable>

      {/* hint */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 'calc(var(--safe-bottom) + 14px)',
          textAlign: 'center',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 12,
          letterSpacing: '0.04em',
          color: 'rgba(255,248,236,0.7)',
          pointerEvents: 'none',
        }}
      >
        {scale === 1 ? 'Tap image to zoom · tap outside to close' : `${scale}× · tap image to zoom · tap outside to close`}
      </div>
    </div>
  );
}
